<script setup lang="ts">
import { computed, ref, watch } from 'vue';

/**
 * Avatar dell'utente: mostra l'immagine profilo (proxy same-origin
 * `/api/utenti/:id/avatar`) quando presente, altrimenti le iniziali su fondo
 * brand. Se l'immagine non carica, ripiega sulle iniziali. Usato ovunque
 * compaia un utente (shell, tabelle, account) per un rendering coerente.
 */
const props = withDefaults(
  defineProps<{
    nome?: string;
    cognome?: string;
    /** Ripiego quando manca il nome (es. principal di servizio): iniziali da qui. */
    email?: string;
    id?: number | null;
    haImmagine?: boolean;
    /** Diametro in px. */
    dimensione?: number;
    /** Cambia per forzare il ricaricamento dell'immagine (cache-bust dopo upload). */
    versione?: string | number;
  }>(),
  { dimensione: 32 },
);

const erroreImg = ref(false);
// Un cambio di id/versione ridà una chance all'immagine (reset del fallback).
watch(
  () => [props.id, props.versione, props.haImmagine],
  () => (erroreImg.value = false),
);

const mostraImg = computed(() => props.haImmagine === true && props.id != null && !erroreImg.value);
const src = computed(() => {
  if (props.id == null) return '';
  const v = props.versione != null ? `?v=${encodeURIComponent(String(props.versione))}` : '';
  return `/api/utenti/${props.id}/avatar${v}`;
});

const iniziali = computed(() => {
  const n = props.nome?.at(0) ?? '';
  const c = props.cognome?.at(0) ?? '';
  const da = `${n}${c}`.toUpperCase();
  if (da) return da;
  return props.email ? props.email.slice(0, 2).toUpperCase() : '—';
});

const stile = computed(() => ({ width: `${props.dimensione}px`, height: `${props.dimensione}px` }));
const stileTesto = computed(() => ({ fontSize: `${Math.round(props.dimensione * 0.36)}px` }));
</script>

<template>
  <img
    v-if="mostraImg"
    :src="src"
    :style="stile"
    class="shrink-0 rounded-full object-cover"
    alt=""
    aria-hidden="true"
    @error="erroreImg = true"
  />
  <span
    v-else
    :style="stile"
    class="grid shrink-0 place-items-center rounded-full bg-[var(--color-primary)] font-semibold text-[var(--color-on-primary)]"
    aria-hidden="true"
  >
    <span :style="stileTesto">{{ iniziali }}</span>
  </span>
</template>
